import { useCallback, useEffect, useState } from 'react';
import { uid } from 'react-uid';
import {
  ReferenceMinimal,
  TransformedFile,
} from './useReferenceFiles/useReferenceFilesTypes';
import { sendFullTextEvent, UploadMethod } from 'util/pendoTrack';
import * as queries from 'query/study/Documents';
import { useFilesForReference } from 'pages/StudyList/components/StudyReferenceFilesProvider';

export type UseFileUploadParams = {
  reference: ReferenceMinimal;
  reviewId: string;
  saveDocument: (
    referenceId: number,
    file: File,
    documentUrl: string
  ) => Promise<{
    id: string;
    authenticatedUrl: string;
    uploadedBy: string;
    createdAt: string;
  }>;
  updateFiles: (files: TransformedFile[]) => void;
};

type useFileUploadReturn = {
  isUploading: boolean;
  uploadingCount: number;
  hasError: boolean;
  clearError: () => void;
  handleUpload: (file: File, uploadMethod: UploadMethod) => void;
};

export const useFileUpload = ({
  reference,
  reviewId,
  saveDocument,
  updateFiles,
}: UseFileUploadParams): useFileUploadReturn => {
  const [uploadingCount, setUploadingCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [pdfFile, setPdfFile] = useState<TransformedFile | null>(null);
  const { files: originalFiles, refresh } = useFilesForReference(reference.id);

  useEffect(() => {
    if (!pdfFile) return;
    // this will update files' progress status on parent component
    updateFiles(_sortFiles([pdfFile, ...originalFiles]));
  }, [pdfFile, originalFiles]);

  const clearError = () => {
    setHasError(false);
  };

  const upload = async (
    file: File,
    uploadMethod: UploadMethod,
    onProgress: (progress: number) => void,
    onSuccess: (file: File, callbackUrl: string) => void,
    onError: (file: File) => void
  ) => {
    setUploadingCount((count) => count + 1);
    setIsUploading(true);
    setHasError(false);
    try {
      const { file: uploadedFile, callbackUrl } = await queries.uploadFile(
        reference.id,
        file.name,
        file,
        reviewId,
        onProgress
      );
      onSuccess(uploadedFile, callbackUrl);
      sendFullTextEvent({ uploadMethod });
    } catch {
      setHasError(true);
      onError(file);
    } finally {
      setUploadingCount((count) => count - 1);
      setIsUploading(false);
    }
  };

  const _addInProgressFile = useCallback((file: File) => {
    const newFile: TransformedFile = {
      id: uid(file),
      file,
      url: null,
      uploadFileName: file.name,
      uploadComplete: false,
    };
    setPdfFile(newFile);
  }, []);

  const _updateFileUploadProgress = useCallback(
    (targetFile: File, progress: number) => {
      const inProgressFile: TransformedFile = {
        id: uid(targetFile),
        file: targetFile,
        url: null,
        uploadFileName: targetFile.name,
        uploadComplete: false,
        progress,
      };
      setPdfFile(inProgressFile);
    },
    []
  );

  const _setFileUploadError = useCallback((failedFile: File) => {
    const failedFileEntry: TransformedFile = {
      id: uid(failedFile),
      file: failedFile,
      url: null,
      uploadFileName: failedFile.name,
      uploadComplete: false,
      uploadError: 'Upload failed',
    };
    setPdfFile(failedFileEntry);
  }, []);

  const _sortFiles = (files: TransformedFile[]) => {
    return [...files].sort((a, b) => {
      if (a.isPrimary) return -1;
      if (b.isPrimary) return 1;
      return 0;
    });
  };

  const handleUpload = useCallback(
    (file: File, uploadMethod: UploadMethod) => {
      _addInProgressFile(file);

      const updateProgress = (progress: number) => {
        _updateFileUploadProgress(file, progress);
      };

      const handleSuccess = async (uploadedFile: File, callbackUrl: string) => {
        try {
          await saveDocument(reference.id, uploadedFile, callbackUrl);
          await refresh();
        } catch {
          handleError(uploadedFile);
        }
      };

      const handleError = (failedFile: File) => {
        _setFileUploadError(failedFile);
      };

      upload(file, uploadMethod, updateProgress, handleSuccess, handleError);
    },
    [
      _addInProgressFile,
      _updateFileUploadProgress,
      _setFileUploadError,
      upload,
      saveDocument,
      reference,
      refresh,
    ]
  );

  return {
    isUploading,
    uploadingCount,
    hasError,
    clearError,
    handleUpload,
  };
};
